// "Get ready": the details that switch features on, and which are still
// missing. Each item says what it unlocks, so it's clear why it matters.

import type { Contact, Household, Profile } from './types.ts';

export interface ReadyItem {
  id: string;
  label: string;
  /** What it switches on. */
  unlocks: string;
  done: boolean;
  /** Where to fix it. */
  route: string;
  /** Needs doing on each person's own phone. */
  perPhone?: boolean;
}

export interface ReadinessInput {
  profile: Profile;
  household: Household;
  contacts: Contact[];
  synced: boolean;
  /** People with phone alerts on (from the server); undefined = unknown. */
  pushPeople?: string[];
  calendar?: boolean;
}

export function readiness({ profile, household, contacts, synced, pushPeople, calendar }: ReadinessInput): ReadyItem[] {
  const f = profile.food ?? {};
  const people = household.people;
  const missingAlerts = pushPeople ? people.filter((p) => !pushPeople.includes(p.id)) : [];
  const items: ReadyItem[] = [
    { id: 'sync', label: 'This device saves to the server', unlocks: 'nothing lost, every device in step', done: synced, route: '/settings/sync' },
    { id: 'dates', label: `${profile.name || 'Arlo'}’s birthday and home date`, unlocks: 'the care plan, age-based routine and countdown', done: !!profile.dob && !!profile.homeDate, route: '/settings' },
    { id: 'vet', label: 'Your vet’s phone number', unlocks: 'one-tap “Call the vet” in Health watch and Emergency', done: contacts.some((c) => c.role === 'vet' && !!c.phone), route: '/health/contacts' },
    { id: 'ooh', label: 'An out-of-hours vet', unlocks: 'the right number after 6pm and at weekends', done: contacts.some((c) => c.role === 'emergency-vet' && !!c.phone), route: '/emergency' },
    { id: 'food', label: 'His food: grams a day, bag size and when you opened it', unlocks: 'meal amounts, and a warning a week before the bag runs out', done: !!f.dailyGrams && !!f.bagKg && !!f.bagOpened, route: '/food' },
    { id: 'rota', label: 'Who does which nights', unlocks: 'the night-trip alarm going to the right phone', done: !!household.rota?.night?.some(Boolean), route: '/settings/household' },
    // Only listed when the server could be asked — "unknown" isn't "not done".
    ...(pushPeople === undefined
      ? []
      : [
          {
            id: 'alerts',
            label: missingAlerts.length ? `Phone alerts for ${missingAlerts.map((p) => p.name).join(' and ')}` : `Phone alerts for ${people.map((p) => p.name).join(' and ') || 'each of you'}`,
            unlocks: 'toilet trips, Health watch and the night trip — even with the app closed',
            done: missingAlerts.length === 0 && people.length > 0,
            route: '/settings/device',
            perPhone: true,
          },
        ]),
    ...(calendar === undefined
      ? []
      : [{ id: 'calendar', label: 'Care dates in a phone calendar', unlocks: 'vaccinations and worming alongside everything else (add it on each phone — this ticks once any calendar has picked it up)', done: calendar, route: '/health' }]),
    { id: 'postcode', label: 'Your postcode', unlocks: 'rain warnings for your street, and walks that start at home', done: household.home?.lat !== undefined, route: '/settings/household' },
    { id: 'chip', label: 'Microchip number and database', unlocks: 'the lost-dog page and vet report', done: !!profile.microchip && !!profile.microchipDatabase, route: '/settings' },
    { id: 'insurance', label: 'Insurer and policy number', unlocks: 'the vet report — and cover from day one', done: !!profile.insurer && !!profile.policyNumber, route: '/settings' },
  ];
  return items;
}
